import React, { useState, useRef, useEffect } from 'react';

interface SqlEditorProps {
    value: string;
    onChange: (val: string) => void;
    columns?: { name: string; dataType?: string }[];
    placeholder?: string;
    className?: string;
}

const KEYWORDS = ['AND', 'OR', 'SELECT', 'FROM', 'WHERE', 'BETWEEN', 'LIKE', 'IN', 'IS', 'NULL', 'NOT', 'TRUE', 'FALSE', 'LIMIT', 'ORDER', 'BY', 'AS', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END'];
// Full list matching PropertiesPanel
const FUNCTIONS = [
    'ABS', 'ROUND', 'CEIL', 'FLOOR', 'SQRT', 'POWER', 'EXP', 'LN', 'LOG', 'MOD', 'PI', 'RANDOM',
    'UPPER', 'LOWER', 'TRIM', 'LENGTH', 'SUBSTRING', 'REPLACE', 'REGEXP_REPLACE', 'LEFT', 'RIGHT', 'REVERSE', 'CONCAT', 'CONCAT_WS', 'STRING',
    'CURRENT_DATE', 'NOW', 'DATE_TRUNC', 'DATE_PART', 'AGE', 'STRFTIME', 'DATE_ADD', 'DATE_DIFF', 'TO_TIMESTAMP',
    'MIN', 'MAX', 'COUNT', 'SUM', 'AVG',
    'IF', 'COALESCE', 'NULLIF',
    'CAST', 'TRY_CAST', 'TINYINT', 'DOUBLE',
    'LEAST', 'GREATEST', 'BIT_COUNT'
];

export function SqlEditor({ value, onChange, columns = [], placeholder, className }: SqlEditorProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const preRef = useRef<HTMLPreElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [suggestions, setSuggestions] = useState<{ x: number, y: number, items: string[] } | null>(null);
    const [editorHeight, setEditorHeight] = useState(120);

    // Auto-grow: recalculate height whenever value changes
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const newHeight = Math.max(120, textareaRef.current.scrollHeight);
            textareaRef.current.style.height = `${newHeight}px`;
            setEditorHeight(newHeight);
        }
    }, [value]);

    // Sync scroll
    const handleScroll = () => {
        if (textareaRef.current && preRef.current) {
            preRef.current.scrollTop = textareaRef.current.scrollTop;
            preRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };

    // Syntax Highlighting
    const getHighlightedText = (text: string) => {
        if (!text) return <span className="text-gray-400">{placeholder}</span>;

        // Escape HTML
        let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        // Highlight Strings
        html = html.replace(/'([^']*)'/g, '<span class="text-amber-600">\'$1\'</span>');

        // Revised Simple Tokenizer:
        // We split by standard delimiters but want to keep column names (often double quoted or just plain words)
        const tokens = text.split(/(\s+|\(|\)|,|'[^']*'|[+\-*/%&|<>!=]+)/);

        return tokens.map((token, i) => {
            if (token.startsWith("'") && token.endsWith("'")) return <span key={i} className="text-amber-600">{token}</span>;
            const upper = token.toUpperCase();

            if (KEYWORDS.includes(upper)) return <span key={i} className="text-blue-600 font-bold">{token}</span>;
            if (FUNCTIONS.includes(upper)) return <span key={i} className="text-blue-600 font-bold">{token}</span>;

            // Highlight Columns: User requested "File Name in Black".
            // We strip quotes to match against column list safely.
            const cleanToken = token.replace(/"/g, '');
            const isCol = columns.some(c => c.name === cleanToken);

            if (isCol) {
                // "Black" and distinct
                return <span key={i} className="text-black font-extrabold">{token}</span>;
            }

            return <span key={i} className="text-gray-800">{token}</span>;
        });
    };

    // Autocomplete Logic
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (suggestions) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                // Navigate list (TODO: Add selection state)
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                insertText(suggestions.items[0]); // Naive select first
                setSuggestions(null);
            } else if (e.key === 'Escape') {
                setSuggestions(null);
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        onChange(val);

        // Simple Autocomplete: If typing a word, show matches
        const cursorPos = e.target.selectionStart;
        const textBefore = val.substring(0, cursorPos);
        const words = textBefore.split(/[\s(,]+/);
        const currentWord = words[words.length - 1];

        if (currentWord.length > 0) {
            const matches = columns
                .map(c => c.name)
                .filter(c => c.toLowerCase().startsWith(currentWord.toLowerCase()) && c !== currentWord);

            // Also match keywords & functions
            const kwMatches = KEYWORDS.filter(k => k.startsWith(currentWord.toUpperCase()) && k !== currentWord.toUpperCase());
            const funcMatches = FUNCTIONS.filter(k => k.startsWith(currentWord.toUpperCase()) && k !== currentWord.toUpperCase());

            const allMatches = [...matches, ...kwMatches, ...funcMatches];

            if (allMatches.length > 0) {
                setSuggestions({ x: 0, y: 0, items: allMatches.slice(0, 5) });
            } else {
                setSuggestions(null);
            }
        } else {
            setSuggestions(null);
        }
    };

    const insertText = (text: string) => {
        if (!textareaRef.current) return;
        const val = textareaRef.current.value;
        const pos = textareaRef.current.selectionStart;
        const textBefore = val.substring(0, pos);

        // Find word start
        const words = textBefore.split(/[\s(,]+/);
        const currentWord = words[words.length - 1];
        const startIdx = pos - currentWord.length;

        const newVal = val.substring(0, startIdx) + text + val.substring(pos);
        onChange(newVal);
        setSuggestions(null);

        // Restore focus
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                textareaRef.current.setSelectionRange(startIdx + text.length, startIdx + text.length);
            }
        }, 0);
    };

    return (
        <div
            className={`relative font-mono text-sm border rounded-lg bg-gray-50 ${className}`}
            ref={containerRef}
            style={{ minHeight: 120, height: editorHeight }}
        >
            {/* Backdrop (Highlighting) */}
            <pre
                ref={preRef}
                className="absolute inset-0 p-3 m-0 pointer-events-none whitespace-pre-wrap break-words overflow-hidden"
                style={{ fontFamily: 'monospace', height: editorHeight }}
                aria-hidden="true"
            >
                {getHighlightedText(value)}
            </pre>

            {/* Foreground (Input) */}
            <textarea
                ref={textareaRef}
                className="relative w-full p-3 m-0 bg-transparent text-gray-900 border-none outline-none resize-none overflow-hidden whitespace-pre-wrap break-words"
                style={{
                    fontFamily: 'monospace',
                    // Trick: Text color transparent, but caret visible? 
                    // No, textarea must be visible for caret. 
                    // Common trick: Textarea color transparent, caret-color black.
                    color: 'transparent',
                    caretColor: 'black',
                    background: 'transparent'
                }}
                value={value}
                onChange={handleChange}
                onScroll={handleScroll}
                onKeyDown={handleKeyDown}
                spellCheck={false}
                placeholder={placeholder}
            />

            {/* Autocomplete Dropdown */}
            {suggestions && suggestions.items.length > 0 && (
                <div className="absolute left-2 bottom-2 bg-white border border-gray-200 shadow-lg rounded-md z-20 max-h-32 overflow-auto">
                    <div className="p-1 text-[10px] text-gray-400 bg-gray-50 border-b">Suggested</div>
                    {suggestions.items.map(item => (
                        <button
                            key={item}
                            className="block w-full text-left px-3 py-1 text-xs hover:bg-blue-50 text-gray-700 font-mono"
                            onMouseDown={(e) => { e.preventDefault(); insertText(item); }}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
